import type { Product } from "@/types/product";

/** Cloudinary delivery URL transforms for product listing images. */

const CLOUDINARY_HOST = "res.cloudinary.com";

export type ProductImageVariant = "card" | "thumbnail" | "detail";

const TRANSFORMS: Record<ProductImageVariant, string> = {
  card: "w_400,h_400,c_fill,q_auto,f_auto",
  thumbnail: "w_128,h_128,c_fill,q_auto,f_auto",
  detail: "w_800,h_800,c_limit,q_auto,f_auto",
};

function isCloudinaryUrl(url: string): boolean {
  try {
    return new URL(url).hostname.includes(CLOUDINARY_HOST);
  } catch {
    return false;
  }
}

/**
 * Insert Cloudinary transformation segment into a delivery URL.
 * Non-Cloudinary URLs are returned unchanged.
 */
export function cloudinaryProductImageUrl(
  url: string,
  variant: ProductImageVariant = "card",
): string {
  if (!url?.trim() || !isCloudinaryUrl(url)) {
    return url?.trim() ?? "";
  }

  const transform = TRANSFORMS[variant];
  if (url.includes(transform)) {
    return url;
  }

  const marker = "/upload/";
  const index = url.indexOf(marker);
  if (index === -1) {
    return url;
  }

  const prefix = url.slice(0, index + marker.length);
  const suffix = url.slice(index + marker.length);
  return `${prefix}${transform}/${suffix}`;
}

export function hasProductImage(url: string | undefined | null): boolean {
  return Boolean(url?.trim());
}

export const syncProductImages = (images: string[]): Pick<Product, "image" | "images"> => ({
  images,
  image: images[0] ?? "",
});
